import { axiosInstanceWebsite, axiosInstanceN8N } from "@/services/instance";
import type {
  CreativesWebsitesResponse,
  FunnelCreativesResponse,
  CreativesResponse,
  ProductsInfoCreativeResponse,
} from "../types/creatives.types";

function normalizeCreativesWebsitesResponse(
  data: any
): CreativesWebsitesResponse {
  if (Array.isArray(data)) return data as CreativesWebsitesResponse;
  if (Array.isArray(data?.data)) return data.data as CreativesWebsitesResponse;
  return [];
}

function normalizeFunnelCreativesResponse(data: any): FunnelCreativesResponse {
  if (Array.isArray(data)) return data as FunnelCreativesResponse;
  if (Array.isArray(data?.data)) return data.data as FunnelCreativesResponse;
  return [];
}

function normalizeCreativesResponse(data: any): CreativesResponse {
  if (Array.isArray(data)) return data as CreativesResponse;
  if (Array.isArray(data?.data)) return data.data as CreativesResponse;
  return [];
}

function normalizeProductsInfoCreativeResponse(
  data: any
): ProductsInfoCreativeResponse {
  if (Array.isArray(data)) return data as ProductsInfoCreativeResponse;
  if (Array.isArray(data?.data)) return data.data as ProductsInfoCreativeResponse;
  return [];
}

export const creativesService = {
  async listWebsites(): Promise<CreativesWebsitesResponse> {
    const response = await axiosInstanceWebsite.get(`/ads/creatives/websites`);
    return normalizeCreativesWebsitesResponse(response.data);
  },

  async listByWebsiteId(websiteId: string): Promise<FunnelCreativesResponse> {
    const response = await axiosInstanceWebsite.get(
      `/ads/creatives/websites/${websiteId}`
    );
    return normalizeFunnelCreativesResponse(response.data);
  },

  async listByProductId(productId?: string): Promise<CreativesResponse> {
    const params = productId ? { product_id: productId } : {};
    const response = await axiosInstanceWebsite.get(`/ads/creatives`, {
      params,
    });
    return normalizeCreativesResponse(response.data);
  },

  async listProducts(): Promise<ProductsInfoCreativeResponse> {
    const response = await axiosInstanceWebsite.get(`/ads/creatives/products`);
    return normalizeProductsInfoCreativeResponse(response.data);
  },

  async generateCreative(data: {
    product_name: string;
    product_image: string;
    product_description: string;
    user_prompt: string;
    image_template: string;
    format: string;
    template_id: string;
    color_mode: "auto" | "custom";
    primary_color: string | null;
    background_color: string | null;
    reference_type: "competitor_ad" | "template";
    config: {
      language: string;
    };
  }): Promise<{ s3_url: string }> {
    try {
      const response = await axiosInstanceN8N.post(`/webhook/claudecode`, data, {
        timeout: 180000,
      });

      console.log("[generateCreative] Raw webhook response:", JSON.stringify(response.data, null, 2));

      // The webhook may return the URL in different shapes depending on the flow
      const raw = response.data;
      const s3Url =
        raw?.s3_url ||
        raw?.urls?.[0] ||
        raw?.image_url ||
        raw?.output?.s3_url ||
        raw?.output?.image_url ||
        raw?.data?.s3_url ||
        raw?.data?.image_url ||
        (typeof raw === "string" && raw.startsWith("http") ? raw : null);

      if (!s3Url) {
        console.error("Respuesta del webhook sin URL de imagen:", raw);
        throw new Error("La respuesta del webhook no contiene URL de imagen");
      }

      return { s3_url: s3Url };
    } catch (error: any) {
      console.error("Error en generateCreative:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      throw error;
    }
  },

  async createCreative(websiteId: string, imageUrl: string, format: string): Promise<void> {
    await axiosInstanceWebsite.post(`/ads/creatives/websites/${websiteId}`, {
      image_url: imageUrl,
      format: format,
    });
  },

  async createCreativeByProduct(productId: string, imageUrl: string, format: string): Promise<void> {
    await axiosInstanceWebsite.post(`/ads/creatives/products/${productId}`, {
      image_url: imageUrl,
      format: format,
    });
  },

  async updateCreativeImageUrl(creativeId: number, imageUrl: string): Promise<void> {
    await axiosInstanceWebsite.patch(`/ads/creatives/${creativeId}`, {
      image_url: imageUrl,
    });
  },

  async editCreativeImage(creativeId: number, prompt: string): Promise<{ image_url: string }> {
    const response = await axiosInstanceWebsite.post(`/ads/creatives/${creativeId}/edit`, { prompt }, { timeout: 180000 });
    const data = response.data;
    return { image_url: data.image_url || data.imageUrl || "" };
  },

  async generateCreativesAsync(
    websiteId: string,
    data: {
      product_name: string;
      product_image: string;
      product_description: string;
      templates: Array<{
        user_prompt: string;
        image_template: string;
        format: string;
        template_id: string;
        color_mode: "auto" | "custom";
        primary_color: string | null;
        background_color: string | null;
        reference_type: "competitor_ad" | "template";
        config: { language: string };
      }>;
    }
  ): Promise<CreativesResponse> {
    const response = await axiosInstanceWebsite.post(
      `/ads/creatives/websites/${websiteId}/async`,
      data
    );
    return normalizeCreativesResponse(response.data);
  },
};
